/**
 * AIFeedbackStatusBadge
 * Feature: 071-ai-critique-sessions
 *
 * Displays the status of AI feedback generation for an AI critique session.
 * Shows a progress indicator while AI is generating critiques, and a completion
 * badge when all AI feedback is ready.
 *
 * Phase 10: Added paused state support with play button callback.
 */

import type { AIFeedbackStatus } from '@/graphql/types';

export interface AIFeedbackStatusBadgeProps {
  status: AIFeedbackStatus | null | undefined;
  isPaused?: boolean;
  onResume?: () => void;
  isResuming?: boolean;
  className?: string;
}

/**
 * Badge showing AI feedback generation progress
 *
 * States:
 * - Paused: Shows pause icon and "Paused" text with play button
 * - Loading (pending > 0): Shows spinner and count
 * - Complete (pending = 0, completed > 0): Shows checkmark and count
 * - Failed (failed > 0): Shows warning and count
 *
 * @example
 * ```tsx
 * <AIFeedbackStatusBadge
 *   status={session.aiFeedbackStatus}
 *   isPaused={session.isPaused}
 *   onResume={handleResume}
 * />
 * ```
 */
export function AIFeedbackStatusBadge({
  status,
  isPaused = false,
  onResume,
  isResuming = false,
  className = '',
}: AIFeedbackStatusBadgeProps) {
  // Handle paused state first (takes priority)
  if (isPaused) {
    return (
      <div
        className={`
          inline-flex items-center gap-2 px-2.5 py-1 rounded-full border
          bg-amber-500/10 text-amber-600 border-amber-500/20
          text-sm font-medium
          ${className}
        `}
        title="AI feedback is paused. Click play to start generating critiques."
      >
        {/* Pause icon */}
        <svg
          className="h-3.5 w-3.5"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
        <span>AI Paused</span>
        {onResume && (
          <button
            onClick={onResume}
            disabled={isResuming}
            className="ml-1 p-0.5 rounded-full hover:bg-amber-500/20 transition-colors disabled:opacity-50"
            title="Start AI feedback generation"
          >
            {isResuming ? (
              <svg
                className="animate-spin h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                className="h-4 w-4"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        )}
      </div>
    );
  }

  // Don't render anything if no status and not paused
  if (!status) {
    return null;
  }

  const { pending, completed, failed, total } = status;

  // Determine current state
  const isGenerating = pending > 0;
  const hasFailures = failed > 0;
  const isComplete = pending === 0 && completed > 0;

  // Don't show if no jobs were created
  if (total === 0) {
    return null;
  }

  // Select appropriate styling based on state
  let bgColor = 'bg-muted/10';
  let textColor = 'text-muted';
  let borderColor = 'border-muted/20';
  let icon = null;

  if (isGenerating) {
    bgColor = 'bg-primary/10';
    textColor = 'text-primary';
    borderColor = 'border-primary/20';
    icon = (
      <svg
        className="animate-spin h-3.5 w-3.5 mr-1.5"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );
  } else if (hasFailures) {
    bgColor = 'bg-warning/10';
    textColor = 'text-warning';
    borderColor = 'border-warning/20';
    icon = (
      <svg
        className="h-3.5 w-3.5 mr-1.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    );
  } else if (isComplete) {
    bgColor = 'bg-success/10';
    textColor = 'text-success';
    borderColor = 'border-success/20';
    icon = (
      <svg
        className="h-3.5 w-3.5 mr-1.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M5 13l4 4L19 7"
        />
      </svg>
    );
  }

  // Build status text
  let statusText = '';
  if (isGenerating) {
    statusText = `AI generating (${completed}/${total})`;
  } else if (hasFailures) {
    statusText = `AI: ${completed} done, ${failed} failed`;
  } else if (isComplete) {
    statusText = `AI feedback ready (${completed})`;
  }

  return (
    <div
      className={`
        inline-flex items-center px-2.5 py-1 rounded-full border
        ${bgColor} ${textColor} ${borderColor}
        text-sm font-medium
        ${className}
      `}
      title={`AI Feedback: ${completed} completed, ${pending} pending${failed > 0 ? `, ${failed} failed` : ''}`}
    >
      {icon}
      <span>{statusText}</span>
    </div>
  );
}

export default AIFeedbackStatusBadge;
